import moment from 'moment';
import React, { createContext, useState, useRef, useCallback } from 'react';
import clienteAxios from '../../config/axios';

export const AlumnosContext = createContext();

export const AlumnoProvider = ({ children }) => {
	const [alumnos, setAlumnos] = useState([]);
	const [loading, setLoading] = useState(false);
	const [credencialAlumno, setCredencialAlumno] = useState();
	const isLoadingRef = useRef(false);

	const [data, setData] = useState({
		nombre: '',
		apellido: '',
		dni: '',
		categoria: 'INFANTIL',
		fechaNacimiento: '',
		pago: '',
		mes: '',
		grupo: '',
		activo: true,
		año: new Date().getFullYear(), // Año actual por defecto
	});

	const getAlumnos = useCallback(async (activo = true) => {
		// Evitar llamadas duplicadas simultáneas
		if (isLoadingRef.current) {
			return;
		}

		isLoadingRef.current = true;
		setLoading(true);
		try {
			const res = await clienteAxios.get(`/alumnos/obtener?activo=${activo}`);

			const alumnos = res.data.Alumnos.map((e) => {
				return {
					id: e._id,
					nombre: e.nombre,
					apellido: e.apellido,
					dni: e.dni,
					categoria: e.categoria,
					estado: e.estado,
					fechaNacimiento: e.fechaNacimiento,
					avatar: e.avatar,
					asistencia: e.asistencia,
					pagos: e.pagos,
					grupo: e.grupo,
					activo: e.activo,
				};
			});
			setAlumnos(alumnos);
		} catch (error) {
			console.error(error);
		} finally {
			isLoadingRef.current = false;
			setLoading(false);
		}
	}, []);

	const asistencia = useCallback(async (id, dataForm) => {
		const form = {
			id,
			asistencia: dataForm.asistencia,
		};
		try {
			await clienteAxios.put(`/alumnos/asistencia`, form);
			getAlumnos();
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const pagos = useCallback(async (id, dataForm) => {
		const form = {
			id,
			pagos: dataForm.pagos,
			estado: dataForm.estado,
		};
		try {
			await clienteAxios.put(`/alumnos/pagos`, form);
			alert('Pago Realizado');
			getAlumnos();
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const resetPagos = useCallback(async () => {
		const form = {
			estado: 'debe',
		};
		try {
			await clienteAxios.put(`/alumnos/reset-pagos`, form);
			getAlumnos();
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const createAlumno = useCallback(async (dataForm) => {
		const form = {
			nombre: dataForm.nombre,
			apellido: dataForm.apellido,
			dni: dataForm.dni,
			categoria: dataForm.categoria,
			fechaNacimiento: dataForm.fechaNacimiento,
			estado: 'debe',
			avatar:
				'https://pbs.twimg.com/profile_images/1445078912963420162/UruNaQPN_400x400.jpg',
			asistencia: [],
			grupo: dataForm.grupo,
			activo: dataForm.activo !== undefined ? dataForm.activo : true,
		};

		try {
			await clienteAxios.post(`/alumnos/crear`, form);
			alert('Alumno Creado');
			getAlumnos();
			setData({
				nombre: '',
				apellido: '',
				dni: '',
				categoria: 'INFANTIL',
				fechaNacimiento: '',
				pago: '',
				mes: '',
				grupo: '',
				activo: true,
				año: new Date().getFullYear(),
			});
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const updateAlumno = useCallback(async (id, dataForm) => {
		const form = {
			id,
			nombre: dataForm.nombre,
			apellido: dataForm.apellido,
			dni: dataForm.dni,
			categoria: dataForm.categoria,
			estado: dataForm.estado,
			fechaNacimiento: dataForm.fechaNacimiento,
			avatar: dataForm.avatar,
			asistencia: dataForm.asistencia,
			grupo: dataForm.grupo,
			activo: dataForm.activo,
		};

		try {
			await clienteAxios.put(`/alumnos/actualizar`, form);
			getAlumnos();
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const updateAlumnosBulk = useCallback(async (ids, dataForm) => {
		const form = {
			ids,
			categoria: dataForm.categoria,
			grupo: dataForm.grupo,
			activo: dataForm.activo,
		};

		try {
			await clienteAxios.put(`/alumnos/actualizar-lote`, form);
			getAlumnos();
			alert('Alumnos Actualizados');
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const suspendAlumno = useCallback(async (id) => {
		try {
			await clienteAxios.put(`/alumnos/actualizar`, { id, activo: false });
			getAlumnos();
			alert('Alumno suspendido correctamente');
		} catch (error) {
			console.error(error);
			alert('Hubo un error al suspender al alumno');
		}
	}, [getAlumnos]);

	const deleteAlumno = useCallback(async (id) => {
		const data = { id };

		try {
			await clienteAxios.delete(`/alumnos/borrar`, { data });
			getAlumnos();
		} catch (error) {
			console.error(error);
		}
	}, [getAlumnos]);

	const handleFaltas = useCallback((id) => {
		let update = alumnos.find((e) => {
			return e.id === id;
		});
		let updateData = {
			...update,
			asistencia: new Date().toLocaleDateString(),
		};
		asistencia(id, updateData);
	}, [alumnos, asistencia]);

	const handlePagos = useCallback((id, pago, mesPago, añoPago) => {
		let update = alumnos.find((e) => {
			return e.id === id;
		});

		// Usar año proporcionado o año del estado, o año actual por defecto
		const año = añoPago || data.año || new Date().getFullYear();
		const mes = mesPago || data.mes || 'ENERO';

		let updateData = {
			...update,
			pagos: {
				monto: +pago,
				fecha: new Date().toLocaleDateString(),
				mes: mes,
				año: año,
			},
			estado: 'pago',
		};
		pagos(id, updateData);
	}, [alumnos, data.año, data.mes, pagos]);

	const handleResetPagos = useCallback(() => {
		let monthData = {
			month: moment(new Date()).format('M'),
			year: moment(new Date()).format('YYYY'),
		};
		const currentMonth = JSON.parse(localStorage.getItem('currentMonth'));

		if (!currentMonth) {
			localStorage.setItem('currentMonth', JSON.stringify(monthData));
		} else if (currentMonth && currentMonth.year === monthData.year) {
			alumnos.forEach((alumno) => {
				let pagosArray = [];
				alumno.pagos.forEach((pago) => {
					const mesPago = parseInt(pago.fecha.split('/')[1]);
					pagosArray.push(mesPago);
				});
				if (!pagosArray.includes(currentMonth.month)) {
					resetPagos();
				}
			});
			localStorage.setItem('currentMonth', JSON.stringify(monthData));
		}
	}, [alumnos, resetPagos]);

	const handleChangeAlumno = (event) => {
		console.log('event :', event);
		event.preventDefault();
		setData({
			...data,
			[event.target.name]: event.target.value,
		});
	};

	const getAlumnoById = useCallback(async (id) => {
		try {
			const res = await clienteAxios.get(`/alumnos/${id}`);
			const alumno = res.data.alumno;
			setData({
				id: alumno._id,
				nombre: alumno.nombre,
				apellido: alumno.apellido,
				dni: alumno.dni,
				categoria: alumno.categoria,
				fechaNacimiento: alumno.fechaNacimiento,
				grupo: alumno.grupo || '',
				activo: alumno.activo !== undefined ? alumno.activo : true,
				estado: alumno.estado,
				avatar: alumno.avatar,
				asistencia: alumno.asistencia,
				pagos: alumno.pagos,
			});
		} catch (error) {
			console.error('Error obteniendo alumno por ID:', error);
		}
	}, []);

	const clearFormData = useCallback(() => {
		setData({
			nombre: '',
			apellido: '',
			dni: '',
			categoria: 'INFANTIL',
			fechaNacimiento: '',
			pago: '',
			mes: '',
			grupo: '',
			activo: true,
			año: new Date().getFullYear(),
		});
	}, []);

	const getCredencial = useCallback(async (dni) => {
		try {
			const res = await clienteAxios.get(`/alumnos/credencial?dni=${dni}`);
			const credencial = res.data.credencial;
			// Formatear el alumno para que coincida con el formato esperado
			setCredencialAlumno({
				id: credencial._id,
				nombre: credencial.nombre,
				apellido: credencial.apellido,
				dni: credencial.dni,
				categoria: credencial.categoria,
				fechaNacimiento: credencial.fechaNacimiento,
				avatar: credencial.avatar,
				asistencia: credencial.asistencia,
				pagos: credencial.pagos,
			});
		} catch (error) {
			console.error('Error obteniendo credencial por DNI:', error);
		}
	}, []);

	const contextData = {
		alumnos,
		setAlumnos,
		loading,
		data,
		setData,
		handleChangeAlumno,
		createAlumno,
		getAlumnos,
		getAlumnoById,
		clearFormData,
		updateAlumno,
		updateAlumnosBulk,
		suspendAlumno,
		deleteAlumno,
		handleFaltas,
		asistencia,
		handlePagos,
		handleResetPagos,
		getCredencial,
		credencialAlumno,
	};
	//console.log('AlumnosContext :', contextData);
	return (
		<AlumnosContext.Provider value={contextData}>
			{children}
		</AlumnosContext.Provider>
	);
};
